const BASE_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:8081"
    : process.env.REACT_APP_API_BASE_URL || "https://api.nexagpt.ai";

let isRefreshing = false;
let refreshQueue = [];

const processQueue = (token, error) => {
  refreshQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  refreshQueue = [];
};

const tryRefresh = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) throw new Error("No refresh token");

  const res = await fetch(`${BASE_URL}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });

  const data = await res.json();
  if (!res.ok || !data.data?.accessToken) throw new Error("Refresh failed");

  localStorage.setItem("token", data.data.accessToken);
  return data.data.accessToken;
};

const request = async (path, options = {}, retry = true) => {
  const token = localStorage.getItem("token");

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  // Token expired — attempt silent refresh once
  if (res.status === 401 && retry) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then((newToken) => {
        return request(path, options, false);
      });
    }

    isRefreshing = true;
    try {
      const newToken = await tryRefresh();
      processQueue(newToken, null);
      isRefreshing = false;
      return request(path, options, false);
    } catch (err) {
      processQueue(null, err);
      isRefreshing = false;
      localStorage.clear();
      window.location.href = "/login";
      return;
    }
  }

  if (res.status === 401 && !retry) {
    localStorage.clear();
    window.location.href = "/login";
    return;
  }

  const data = await res.json();
  if (!data.success) throw new Error(data.message || "Request failed");
  return data.data;
};

const upload = async (path, formData) => {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || "Upload failed");
  return data.data;
};

export const api = {
  get: (path) => request(path),
  post: (path, body) => request(path, { method: "POST", body: JSON.stringify(body) }),
  patch: (path, body) => request(path, { method: "PATCH", body: JSON.stringify(body) }),
  put: (path, body) => request(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: (path) => request(path, { method: "DELETE" }),
  upload,
  baseUrl: BASE_URL,
};
